// src/shared/ui/components/header-tabs-fit.ts
// Which header tabs show and which go into "More" (design log 13 Oct, Rule "tabs give way first,
// into More; the active tab always stays"; prototype src/app/shell/zones.ts `fitTabs`). Pure, so it
// is tested without a layout. Widths are the natural widths measured by HeaderTabs.

/** The "More" tab's width, chevron included. */
export const MORE_W = 72;
/** The gap between tabs (= --spacing-6). */
export const TAB_GAP = 24;
/** Room kept free after the last tab when they all fit (= --page-header-tabs-end). */
export const TABS_END = 24;

export interface FitResult {
  /** Indexes of the tabs drawn in the row, in order. */
  shown: number[];
  /** Indexes of the tabs inside the More menu, in order. */
  more: number[];
  /** Every tab fits with the end room to spare. */
  allFit: boolean;
}

/**
 * @param widths natural width of each tab, in order
 * @param room the row's available width
 * @param active index of the selected tab (never put into More while another could go)
 */
export function fitHeaderTabs(widths: readonly number[], room: number, active: number): FitResult {
  const all = widths.map((_, i) => i);
  // Nothing measured yet (first render, or no layout): show everything.
  if (room <= 0 || widths.length === 0 || widths.some((w) => !(w > 0))) return { shown: all, more: [], allFit: false };
  const total = widths.reduce((sum, w) => sum + w + TAB_GAP, 0);
  if (total <= room - TABS_END) return { shown: all, more: [], allFit: true };
  const shown: number[] = [];
  let used = MORE_W;
  for (let i = 0; i < widths.length; i++) {
    if (used + widths[i] + TAB_GAP > room) break;
    used += widths[i] + TAB_GAP;
    shown.push(i);
  }
  if (active >= 0 && !shown.includes(active)) {
    // Make room for the active tab by taking the last shown ones back out.
    while (shown.length > 0 && used + widths[active] + TAB_GAP > room) {
      used -= widths[shown.pop() as number] + TAB_GAP;
    }
    shown.push(active);
    shown.sort((a, b) => a - b);
  }
  return { shown, more: all.filter((i) => !shown.includes(i)), allFit: false };
}
